import clsx from "clsx";
import { useContext, useEffect, useRef, useState } from "react";
import {
  SvgCaseCustomer,
  SvgMapMarker,
  SvgCalendar,
  SvgShare,
  SvgCommonDownload,
  SvgShareToDrive,
  SvgAddCaseNote,
  SvgClose,
  SvgGoToCase,
} from "ui/svgs";
import Notes from "ui/cases/notes";
import {
  formatAlertTagSDKToDisplayWithDesc,
  formatCaseStatusSDKToDisplay,
  getCaseStatusStyles,
  captureFramesFromVideo,
} from "lib/utils";
import { Popover } from "react-tiny-popover";
import { CASE_STATUS_SDK, INVESTIGATE_TAG_SDK, USER_ROLE } from "lib/constants";
import { AlertDataType } from "lib/types";
import "pages/dashboard/cases/reportPage.scss";
import { AuthContext } from "context/AuthContext";
import { showToast } from "ui/components/common";
import { Spin, Modal, Progress, Tooltip } from "antd";
import { pdf } from "@react-pdf/renderer";
import UploadToDriveModal from "ui/cases/upload-to-drive";
import moment from "moment";
import JSZip from "jszip";
import { useService } from "pages/dashboard/alerts/service";
import PdfDocument from "ui/cases/export-case-document";
import ShareCaseModal from "ui/cases/share-case-modal";
import { useNavigate } from "react-router-dom";
import NoteAudio from "ui/dealer-focus/note-audio";

type DataType = {
  time: number;
  action: string;
  note?: string;
  noteOwner?: string;
  audioUrl?: string;
};

export default function CaseDetail(props) {
  const { privileges } = useContext(AuthContext);
  const { activeCase, activeAlert } = props;
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isExportPdfModalOpen, setIsExportPdfModalOpen] = useState(false);
  const [isExportZipModalOpen, setIsExportZipModalOpen] = useState(false);
  const [isUploadToDriveModalOpen, setIsUploadToDriveModalOpen] =
    useState(false);
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const navigate = useNavigate();
  const { getSnapShotByAlertObj } = useService();
  const [alertSnapshotMap, setAlertSnapshotMap] = useState<Map<string, string>>(
    new Map(),
  );
  const [archiveSnapshotMap, setArchiveSnapshotMap] = useState<
    Map<string, string>
  >(new Map());
  const [isDownloadPopoverOpen, setIsDownloadPopoverOpen] = useState(false);

  const [data, setData] = useState<DataType[]>([]);

  const zipExportModalOpenRef = useRef(false);

  const isCaseDealer = privileges.includes("caseDealer");

  const successArchiveCount = activeCase?.archives?.filter(
    (archive) => archive.status === "succeed",
  ).length;

  useEffect(() => {
    if (!activeCase || (!isExportPdfModalOpen && !isExportZipModalOpen)) {
      setAlertSnapshotMap(new Map());
      setArchiveSnapshotMap(new Map());
      return;
    }
    const captureArchiveFrames = async () => {
      const archiveSnapshotMap = new Map<string, string>();
      for (const archive of activeCase.archives) {
        if (archive?.status !== "succeed") {
          continue;
        }
        const data = await captureFramesFromVideo(archive?.s3Files?.at(0)?.url);
        archiveSnapshotMap.set(archive.id, data);
      }

      setArchiveSnapshotMap(archiveSnapshotMap);
    };
    if (isExportPdfModalOpen || isExportZipModalOpen) {
      captureArchiveFrames();
    }

    activeCase.alerts.forEach((alert) => {
      getSnapShotByAlertObj(alert, { case_id: activeCase?.id })
        .then((data) => {
          setAlertSnapshotMap((pre) => {
            pre.set(alert.id, URL.createObjectURL(data));
            return new Map(pre);
          });
        })
        .catch((e) => {
          setAlertSnapshotMap((pre) => {
            pre.set(alert.id, "");
            return new Map(pre);
          });
        });
    });
  }, [activeCase, isExportPdfModalOpen, isExportZipModalOpen]);

  useEffect(() => {
    if (
      !activeCase ||
      alertSnapshotMap.size < activeCase?.alerts?.length ||
      archiveSnapshotMap.size < successArchiveCount
    ) {
      return;
    }

    const downloadPDF = async () => {
      // 生成 PDF Blob
      const blob = await pdf(
        <PdfDocument
          caseInfo={activeCase}
          alertSnapshotMap={alertSnapshotMap}
          archiveSnapshotMap={archiveSnapshotMap}
        />,
      ).toBlob();
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;

      link.download =
        "Case Report:" +
        activeCase.displayId +
        "_" +
        moment().format("YYYY-MM-DD HH:mm:ss") +
        ".pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setIsExportPdfModalOpen(false);
    };

    const createZip = async (zip) => {
      const blob = await pdf(
        <PdfDocument
          caseInfo={activeCase}
          alertSnapshotMap={alertSnapshotMap}
          archiveSnapshotMap={archiveSnapshotMap}
        />,
      ).toBlob();
      zip.file(`Case_${activeCase.displayId}.pdf`, blob);
      const invalidCharsPattern = /[\\/:*?"<>|]/g;
      for (let i = 0; i < activeCase?.alerts?.length; i++) {
        if (!zipExportModalOpenRef.current) {
          return;
        }
        const alert = activeCase?.alerts[i];

        const videoName =
          `${alert.camera?.name}_${moment(Number(alert.createdAt)).format("YY_MM_DD_HH_mm")}.mp4`.replace(
            invalidCharsPattern,
            "_",
          );

        try {
          const response = await fetch(alert?.video?.url);
          const videoBlob = await response.blob();

          zip.folder("Alerts").file(videoName, videoBlob);
        } catch (error) {
          console.error("下载视频失败:", error);
          showToast(1, "Failed to export case. Please try again later.");
          return;
        }
      }
      for (let i = 0; i < activeCase?.archives?.length; i++) {
        if (!zipExportModalOpenRef.current) {
          return;
        }
        const archive = activeCase?.archives[i];
        if (archive.status !== "succeed") {
          continue;
        }
        const videoName =
          `${archive.uploadParams?.cameraName}_${moment(Number(archive.createdAt)).format("YY_MM_DD_HH_mm")}.mp4`.replace(
            invalidCharsPattern,
            "_",
          );
        try {
          const response = await fetch(archive?.s3Files?.at(0)?.url);
          const videoBlob = await response.blob();

          zip.folder("Archives").file(videoName, videoBlob);
        } catch (error) {
          console.error("下载视频失败:", error);
          showToast(1, "Failed to export case. Please try again later.");
          return;
        }
      }
      // create zip
      try {
        const content = await zip.generateAsync({ type: "blob" });
        const downloadLink = document.createElement("a");
        const url = URL.createObjectURL(content);
        downloadLink.href = url;
        downloadLink.download = `Case_${activeCase?.displayId}.zip`;
        downloadLink.click();
        URL.revokeObjectURL(url);
        setIsExportZipModalOpen(false);
      } catch (error) {
        console.error("压缩视频失败:", error);
        showToast(1, "Failed to zip case. Please try again later.");
      }
    };
    if (isExportPdfModalOpen) {
      downloadPDF();
    } else if (isExportZipModalOpen) {
      const zip = new JSZip();
      createZip(zip);
    }
  }, [alertSnapshotMap, archiveSnapshotMap]);

  useEffect(() => {
    if (!activeCase) {
      return;
    }
    const tableData: DataType[] = [];
    activeCase.alerts.forEach((alert: AlertDataType) => {
      const alertNotes = alert?.notes?.map(
        ({ createdAt, note, user, audioFiles }) => {
          return {
            time: 1000 * createdAt,
            note,
            noteOwner: user.name,
            action: "Add Alert Note",
            audioUrl: audioFiles?.at(0)?.url,
          };
        },
      );
      tableData.push(...alertNotes);
    });
    const caseNotes = activeCase?.notes?.map(({ createdAt, note, user }) => {
      return {
        time: 1000 * createdAt,
        note,
        noteOwner: user.name,
        action: "Add Case Note",
      };
    });

    const caseHistory = activeCase?.statusHistory?.map(
      ({ createdAt, status }) => {
        return {
          time: +createdAt,
          action: status,
        };
      },
    );

    tableData.push(...caseNotes);
    tableData.push({
      time: +activeCase?.alerts?.at(0)?.createdAt,
      action: "Case Detected",
    });
    tableData.push(...caseHistory);

    tableData.sort((a, b) => a.time - b.time);
    setData(tableData);
  }, [activeCase]);

  const handleCancelExport = () => {
    setIsExportPdfModalOpen(false);
  };

  const handleCancelExportZip = () => {
    setIsExportZipModalOpen(false);
    zipExportModalOpenRef.current = false;
  };
  function handleGotoCase(e) {
    e.stopPropagation();
    navigate(`/dashboard/cases?page=1&displayId=${activeCase.displayId}`);
  }
  if (!activeCase) {
    return (
      <div className="h-full flex-grow flex justify-center items-center">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div
      className="px-1 h-full flex-grow flex flex-col max-h-full case-report py-6 overflow-y-auto"
      //onWheel={handleWheel}
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-center font-semibold text-xl gap-2">
          Case Report&nbsp;{activeCase?.displayId}
          <Tooltip title="Go to Case">
            <button
              className="w-8 h-8 rounded-full hover:bg-[#DCE7FD] flex items-center justify-center fill-neutral-800"
              onClick={(e) => {
                handleGotoCase(e);
              }}
            >
              <SvgGoToCase width="22" className=" flex-shrink-0" />
            </button>
          </Tooltip>
        </div>
        <div className="flex justify-center items-center">
          <div className="flex gap-3  font-semibold flex-wrap">
            <p
              className="px-2 rounded-lg "
              style={getCaseStatusStyles(activeCase?.status as CASE_STATUS_SDK)}
            >
              {formatCaseStatusSDKToDisplay(
                activeCase?.status as CASE_STATUS_SDK,
              )}
            </p>
            <p className="bg-[#EEEEEE] text-neutral-500 rounded-lg px-2">
              {formatAlertTagSDKToDisplayWithDesc(
                activeCase?.tag?.name as INVESTIGATE_TAG_SDK,
                activeCase?.tag?.desc,
              )}
            </p>
          </div>
        </div>
        <div className="flex item-center justify-center">
          {isCaseDealer && (
            <>
              <SvgCaseCustomer
                width="17"
                className="flex-shrink-0 fill-zinc-500"
              />
              <p className="max-w-48 overflow-hidden break-words text-ellipsis text-nowrap break-all ml-1 mr-10">
                {activeCase?.customer?.accountName || "-"}
              </p>
            </>
          )}

          <SvgMapMarker width="16" className="flex-shrink-0 fill-zinc-500" />

          <p className="max-w-48 overflow-hidden break-words text-ellipsis text-nowrap break-all mr-3 flex-shrink-0">
            {activeAlert?.site?.name}
          </p>
          <span className="text-[#BDBDBD]  mr-3">|</span>
          <Tooltip
            title={
              activeAlert?.location?.address +
              "," +
              activeAlert?.location?.zipcode
            }
          >
            <p className=" overflow-hidden break-words text-ellipsis text-nowrap break-all">
              {activeAlert?.location?.address}, {activeAlert?.location?.zipcode}
            </p>
          </Tooltip>
        </div>
        <div className="flex justify-center items-center gap-2">
          <SvgCalendar width="17" className="fill-zinc-500 flex-shrink-0" />
          {moment(+activeCase?.createdAt).format("MMM DD[,] yyyy")}
        </div>
        <div className="flex justify-center mt-2">
          <Tooltip title="Add Note">
            <button
              className={clsx(
                "mr-2 px-6 py-3 items-center rounded-2xl bg-blue-500 text-base font-medium text-white transition-colors hover:bg-blue-400 disabled:bg-blue-400 disabled:hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600",
              )}
              onClick={() => setIsAddNoteModalOpen(true)}
            >
              <SvgAddCaseNote width="18" className="fill-white" />
            </button>
          </Tooltip>
          <Popover
            isOpen={isDownloadPopoverOpen}
            positions={["bottom", "left", "right", "top"]}
            onClickOutside={() => setIsDownloadPopoverOpen(false)}
            content={
              <div
                className="bg-white rounded-2xl py-2 w-[153px] shadow"
                onMouseOut={() => setIsDownloadPopoverOpen(false)}
                onMouseOver={() => setIsDownloadPopoverOpen(true)}
              >
                <button
                  className="block w-full hover:bg-gray-100 h-12 text-neutral-800"
                  onClick={() => {
                    setIsExportPdfModalOpen(true);
                  }}
                >
                  Export PDF
                </button>
                <button
                  className="block w-full hover:bg-gray-100 h-12 text-neutral-800"
                  onClick={() => {
                    setIsExportZipModalOpen(true);
                    zipExportModalOpenRef.current = true;
                  }}
                >
                  Export Case ZIP
                </button>
              </div>
            }
          >
            <div>
              <Tooltip title="Export">
                <button
                  className={clsx(
                    "mr-2 px-6 py-3 items-center rounded-2xl bg-blue-500 text-base font-medium text-white transition-colors hover:bg-blue-400 disabled:bg-blue-400 disabled:hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600",
                  )}
                  onClick={() => {
                    setIsDownloadPopoverOpen(true);
                  }}
                >
                  <SvgCommonDownload width="17" className="fill-white" />
                </button>
              </Tooltip>
            </div>
          </Popover>
          <Tooltip title="Share Case">
            <button
              className={clsx(
                "mr-2 px-6 py-3 items-center rounded-2xl bg-blue-500 text-base font-medium text-white transition-colors hover:bg-blue-400 disabled:bg-blue-400 disabled:hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600",
              )}
              onClick={() => setIsShareModalOpen(true)}
            >
              <SvgShare width="18" className="fill-white" />
            </button>
          </Tooltip>
          <Tooltip title="Share to Google Drive">
            <button
              className={clsx(
                "mr-2 px-6 py-3 items-center rounded-2xl bg-blue-500 text-base font-medium text-white transition-colors hover:bg-blue-400 disabled:bg-blue-400 disabled:hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600",
              )}
              onClick={() => setIsUploadToDriveModalOpen(true)}
            >
              <SvgShareToDrive width="17" className="fill-white" />
            </button>
          </Tooltip>
        </div>

        {data?.map((e) => {
          return (
            <div className="mt-4 rounded-2xl bg-[#F6F8FA] flex p-4 gap-6">
              <div className="basic-[150px] flex-shrink-0">
                <p
                  className="font-semibold py-0.5 px-2"
                  style={getCaseStatusStyles(e?.action as CASE_STATUS_SDK)}
                >
                  {formatCaseStatusSDKToDisplay(e.action as CASE_STATUS_SDK)}
                </p>
                <p className="pl-2 mt-1 text-sm">
                  {moment(e.time).format("MMM hh:mm A")}
                </p>
              </div>
              <div className="flex-grow">
                <p className="">{e.note}</p>
                {e.audioUrl && <NoteAudio url={e.audioUrl} />}
              </div>
            </div>
          );
        })}

        <ShareCaseModal
          caseId={activeCase?.id}
          onRequestClose={() => setIsShareModalOpen(false)}
          isOpen={isShareModalOpen}
        />
        <Modal
          title={<p className="text-lg font-semibold">Generating Report PDF</p>}
          open={isExportPdfModalOpen}
          closable={false}
          centered
          footer={
            <button
              className="ml-4 rounded-2xl px-6 py-4 bg-blue-500 text-sm font-medium text-white transition-colors hover:bg-blue-400 disabled:hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 disabled:opacity-50"
              onClick={handleCancelExport}
            >
              Cancel Export
            </button>
          }
        >
          <>
            <div className="mt-6 flex justify-between items-center">
              <p className="font-semibold text-base">Preparing your report…</p>
              <p className="text-neutral-800 text-base">
                {Math.floor(
                  ((alertSnapshotMap.size + archiveSnapshotMap.size) /
                    (activeCase?.alerts?.length + successArchiveCount)) *
                    100,
                )}
                %
              </p>
            </div>
            <Progress
              percent={Math.floor(
                ((alertSnapshotMap.size + archiveSnapshotMap.size) /
                  (activeCase?.alerts?.length + successArchiveCount)) *
                  100,
              )}
              showInfo={false}
              strokeColor={"#3E83F6"}
            />
            <p>Once completed, the PDF will automatically download.</p>
          </>
        </Modal>
        <Modal
          title={null}
          open={isExportZipModalOpen}
          closable={false}
          centered
          footer={
            <button
              className="ml-4 rounded-2xl px-6 py-4 bg-blue-500 text-sm font-medium text-white transition-colors hover:bg-blue-400 disabled:hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 disabled:opacity-50"
              onClick={handleCancelExportZip}
            >
              Cancel Export
            </button>
          }
        >
          <div className="mt-6 mb-2 flex flex-col items-center justify-center">
            <div>
              <div
                className="ml-1 text-blue-500  inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                role="status"
              ></div>
            </div>
            <p className="text-lg font-semibold mt-2">
              Preparing your case ZIP file...
            </p>
            <p className="mt-2">
              Once completed, the case ZIP will automatically download.
            </p>
          </div>
        </Modal>
        <Modal
          title={
            <div className="flex justify-between">
              <p>Case Notes</p>
              <button
                className="rounded-full w-6 h-6 flex items-center justify-center fill-[#717171] hover:fill-[#8b8989] hover:bg-neutral-100"
                onClick={() => setIsAddNoteModalOpen(false)}
              >
                <SvgClose width="13" />
              </button>
            </div>
          }
          className="add-case-note-modal"
          open={isAddNoteModalOpen}
          closable={false}
          onClose={() => setIsAddNoteModalOpen(false)}
          centered
          footer={null}
        >
          <Notes caseId={activeCase?.id} maxHeight={500} />
        </Modal>
        <UploadToDriveModal
          caseInfo={activeCase}
          open={isUploadToDriveModalOpen}
          onClose={() => {
            setIsUploadToDriveModalOpen(false);
          }}
        />
      </div>
    </div>
  );
}
